import { useEffect, useReducer } from 'react';
import { Search, X } from 'lucide-react';

import { Input } from '@/components/ui/input';

import { useDebounce } from '@/hooks/useDebounce';

type Props = {
  placeholder?: string;
  onFilter: (filterValue: string) => void;
  onClear?: () => void;
  debounceValue?: number;
  initialValue?: string;
};

/**
 *
 * A reusable filter component with a search input that debounces user input.
 * @param placeholder - Placeholder text for the search input.
 * @param onFilter - Callback function to handle filtering logic.
 * @param debounceValue - Debounce delay in milliseconds (default is 800ms).
 * @return A filter component with a search input.
 *
 */
export const Filter = ({
  placeholder,
  initialValue,
  onFilter,
  debounceValue = 800,
  onClear,
}: Props) => {
  const [searchValue, setSearchValue] = useReducer(
    (_state: string, nextValue: string) => nextValue,
    initialValue || ''
  );
  const debouncedSearchValue = useDebounce(searchValue, debounceValue);

  useEffect(() => {
    if (debouncedSearchValue.trim() !== '') {
      onFilter(debouncedSearchValue);
    } else {
      onClear?.();
    }
  }, [debouncedSearchValue, onFilter, onClear]);

  useEffect(() => {
    setSearchValue(initialValue || '');
  }, [initialValue]);

  return (
    <div className="flex-1 max-w-md">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder={placeholder}
          value={searchValue}
          onChange={e => setSearchValue(e.target.value)}
          className="pl-10 pr-10"
        />
        {searchValue && (
          <button
            onClick={() => {
              setSearchValue('');
              onClear?.();
            }}
            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
            aria-label="Clear search"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>
    </div>
  );
};

export const FilterResultStatus = ({
  resultsCount,
  onReset,
  message,
}: {
  resultsCount: number;
  onReset: () => void;
  message: string;
}) => {
  return (
    <div className="flex items-center gap-2 text-xs text-muted-foreground">
      <span>
        {resultsCount === 0
          ? message
          : `Showing ${resultsCount} of ${resultsCount} result${resultsCount !== 1 ? 's' : ''}`}
      </span>
      <button onClick={onReset} className="text-primary hover:underline">
        Clear filters
      </button>
    </div>
  );
};
